import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import {
  AudioFile as AudioFileIcon,
  Book as BookIcon,
  Archive as ArchiveIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { formatFirestoreDate } from '../utils/dateUtils';
// TODO: Import proper functions when implementing file list fetching

interface FileUpload {
  id: string;
  fileName: string;
  fileType: 'm4b' | 'epub' | 'zip';
  fileSize: number;
  uploadDate: Date;
  downloadURL: string;
  extractedData?: any;
  processingStatus: 'pending' | 'processing' | 'completed' | 'error';
  errorMessage?: string;
}

const FileList: React.FC = () => {
  const [files, setFiles] = useState<FileUpload[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFiles = async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: Implement proper file list fetching
      // For now, we'll show an empty list
      // This component should show files that were uploaded, not titles from Firestore
      setFiles([]);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const getFileIcon = (fileType: string) => {
    switch (fileType?.toLowerCase()) {
      case 'm4b':
        return <AudioFileIcon color="primary" />;
      case 'epub':
        return <BookIcon color="primary" />;
      case 'zip':
        return <ArchiveIcon color="primary" />;
      default:
        return <AudioFileIcon color="primary" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'processing':
        return <CircularProgress size={20} />;
      case 'pending':
        return <ScheduleIcon color="warning" />;
      default:
        return <ScheduleIcon color="warning" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      case 'processing':
        return 'info';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date: any): string => {
    return formatFirestoreDate(date);
  };

  const renderExtractedData = (file: FileUpload) => {
    if (!file.extractedData) return null;

    const data = file.extractedData;

    switch (file.fileType) {
      case 'epub':
        return (
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Metadados do E-book
              </Typography>
              <Grid container spacing={2}>
                {data.title && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Título
                    </Typography>
                    <Typography variant="body1">{data.title}</Typography>
                  </Grid>
                )}
                {data.author && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Autor
                    </Typography>
                    <Typography variant="body1">{data.author}</Typography>
                  </Grid>
                )}
                {data.language && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Idioma
                    </Typography>
                    <Typography variant="body1">{data.language}</Typography>
                  </Grid>
                )}
                {data.publisher && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Editora
                    </Typography>
                    <Typography variant="body1">{data.publisher}</Typography>
                  </Grid>
                )}
                {data.chapterCount && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Capítulos
                    </Typography>
                    <Typography variant="body1">{data.chapterCount}</Typography>
                  </Grid>
                )}
                {data.subjects && data.subjects.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Assuntos
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {data.subjects.map((subject: string, index: number) => (
                        <Chip key={index} label={subject} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        );

      case 'zip':
        return (
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Conteúdo do Arquivo
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total de Arquivos
                  </Typography>
                  <Typography variant="body1">{data.fileCount}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Tamanho Total
                  </Typography>
                  <Typography variant="body1">{formatFileSize(data.totalSize)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Tipos de Conteúdo
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {data.containsAudio && <Chip label="Áudio" color="primary" size="small" />}
                    {data.containsEbooks && <Chip label="E-books" color="secondary" size="small" />}
                    {data.containsImages && <Chip label="Imagens" color="info" size="small" />}
                  </Box>
                </Grid>
                {data.files && data.files.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Arquivos (primeiros 10)
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
                      {data.files.slice(0, 10).map((file: any, index: number) => (
                        <Typography key={index} variant="caption" display="block">
                          {file.name} ({formatFileSize(file.size)})
                        </Typography>
                      ))}
                      {data.files.length > 10 && (
                        <Typography variant="caption" color="text.secondary">
                          ... e mais {data.files.length - 10} arquivos
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        );

      case 'm4b':
        return (
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Metadados do Audiobook
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data.metadata?.note || 'Metadados básicos extraídos'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Tamanho: {formatFileSize(data.fileSize)}
              </Typography>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Arquivos Processados ({files.length})</Typography>
        <IconButton onClick={fetchFiles} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erro ao carregar arquivos: {error}
        </Alert>
      )}

      {!loading && files.length === 0 && <Alert severity="info">Nenhum arquivo processado encontrado. Faça upload de arquivos para começar.</Alert>}

      {!loading && files.length > 0 && (
        <List>
          {files.map((file, index) => (
            <React.Fragment key={file.id}>
              <ListItem alignItems="flex-start">
                <ListItemIcon>{getFileIcon(file.fileType)}</ListItemIcon>

                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        {file.fileName}
                      </Typography>
                      <Chip label={(file.fileType || 'unknown').toUpperCase()} size="small" variant="outlined" />
                      <Chip
                        label={file.processingStatus}
                        size="small"
                        color={getStatusColor(file.processingStatus) as any}
                        icon={getStatusIcon(file.processingStatus)}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Tamanho: {formatFileSize(file.fileSize)} | Upload: {formatDate(file.uploadDate)}
                      </Typography>

                      {file.errorMessage && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                          Erro: {file.errorMessage}
                        </Typography>
                      )}

                      {renderExtractedData(file)}
                    </Box>
                  }
                />

                <IconButton href={file.downloadURL} target="_blank" rel="noopener noreferrer" color="primary">
                  <DownloadIcon />
                </IconButton>
              </ListItem>

              {index < files.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default FileList;
